export interface PinboardRoutePost {
  id: number;
  iso?: string;
  place?: string;
  activity?: string;
}

export interface CreateMapAndListPngBlobInput {
  mapContainer: HTMLDivElement | null;
  routePosts: PinboardRoutePost[];
  pinnedPostIdSet: Set<number>;
  backendUrl: string | undefined;
  resolveCssVariables: (rawSvg: string, svgElement: SVGSVGElement) => string;
}

const waitForDoubleAnimationFrame = (): Promise<void> => {
  return new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
};

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      const result = fileReader.result;
      if (typeof result === 'string') {
        resolve(result);
        return;
      }
      reject(new Error('Unable to convert blob to data URL.'));
    };
    fileReader.onerror = () => reject(new Error('Unable to read blob data.'));
    fileReader.readAsDataURL(blob);
  });
};

const inlineSvgImages = async (svgClone: SVGSVGElement): Promise<void> => {
  const imageElements = Array.from(svgClone.querySelectorAll('image'));
  if (imageElements.length === 0) return;

  await Promise.all(
    imageElements.map(async (imageElement) => {
      const hrefValue = imageElement.getAttribute('href') || imageElement.getAttribute('xlink:href') || '';
      if (!hrefValue || hrefValue.startsWith('data:')) return;

      try {
        const response = await fetch(hrefValue, { mode: 'cors' });
        if (!response.ok) return;
        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);
        imageElement.setAttribute('href', dataUrl);
        imageElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', dataUrl);
      } catch {
        // Keep original href when inlining fails.
      }
    }),
  );
};

export async function waitForMapRenderReady(
  mapContainer: HTMLDivElement | null,
  selectedCountriesCount: number,
  timeoutMs: number = 2000,
): Promise<void> {
  const startedAt = Date.now();

  await new Promise<void>((resolve) => {
    const tick = () => {
      const svgElement = mapContainer?.querySelector('svg');
      if (!svgElement) {
        if (Date.now() - startedAt >= timeoutMs) {
          resolve();
          return;
        }
        window.requestAnimationFrame(tick);
        return;
      }

      const expectedGeography = selectedCountriesCount === 0 ? 0 : 1;
      const geographyCount = svgElement.querySelectorAll('.rsm-geography').length;
      const hasExpectedGeometry = geographyCount >= expectedGeography;

      if (hasExpectedGeometry || Date.now() - startedAt >= timeoutMs) {
        resolve();
        return;
      }

      window.requestAnimationFrame(tick);
    };

    tick();
  });

  await waitForDoubleAnimationFrame();
}

export async function createMapAndListPngBlob({
  mapContainer,
  routePosts,
  pinnedPostIdSet,
  backendUrl,
  resolveCssVariables,
}: CreateMapAndListPngBlobInput): Promise<Blob> {
  const svgElement = mapContainer?.querySelector('svg');
  if (!svgElement) {
    throw new Error('Map SVG not found for export/share.');
  }

  const svgClone = svgElement.cloneNode(true) as SVGSVGElement;
  svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  await inlineSvgImages(svgClone);

  const serializer = new XMLSerializer();
  const rawSvg = serializer.serializeToString(svgClone);
  const resolvedSvg = resolveCssVariables(rawSvg, svgElement);

  const svgBlob = new Blob([resolvedSvg], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = new Image();
    const imageLoadPromise = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Unable to render map image for export/share.'));
    });
    image.src = svgUrl;
    await imageLoadPromise;

    const logoUrl = backendUrl ? `${backendUrl}/django-static/logo/logo.png` : '/django-static/logo/logo.png';
    const logoImage = new Image();
    logoImage.crossOrigin = 'anonymous';
    const logoLoadPromise = new Promise<boolean>((resolve) => {
      logoImage.onload = () => resolve(true);
      logoImage.onerror = () => resolve(false);
    });
    logoImage.src = logoUrl;
    const hasLogo = await logoLoadPromise;

    const sourceWidth = svgElement.clientWidth || 900;
    const sourceHeight = svgElement.clientHeight || 450;
    const scale = 2;
    const headerHeight = 62;
    const listPanelHeight = Math.max(140, 40 + routePosts.length * 24);
    const canvas = document.createElement('canvas');
    canvas.width = sourceWidth * scale;
    canvas.height = (headerHeight + sourceHeight + listPanelHeight) * scale;
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Unable to create canvas context for export/share.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const headerHeightPx = headerHeight * scale;
    context.fillStyle = '#f8fafc';
    context.fillRect(0, 0, canvas.width, headerHeightPx);
    context.strokeStyle = '#d8d8d8';
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(0, headerHeightPx);
    context.lineTo(canvas.width, headerHeightPx);
    context.stroke();

    if (hasLogo) {
      const logoSize = 40 * scale;
      const logoY = (headerHeightPx - logoSize) / 2;
      context.drawImage(logoImage, 12 * scale, logoY, logoSize, logoSize);
    }

    context.fillStyle = '#0f172a';
    context.font = `bold ${16 * scale}px Arial`;
    context.fillText('Living Out Of A Backpack', 60 * scale, 26 * scale);
    context.fillStyle = '#334155';
    context.font = `${11 * scale}px Arial`;
    context.fillText('World Travel Pinboard Export', 60 * scale, 44 * scale);

    const mapTop = headerHeightPx;
    context.drawImage(image, 0, mapTop, sourceWidth * scale, sourceHeight * scale);

    const panelTop = mapTop + sourceHeight * scale;
    context.fillStyle = '#f7f7f7';
    context.fillRect(0, panelTop, canvas.width, listPanelHeight * scale);
    context.strokeStyle = '#d8d8d8';
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(0, panelTop);
    context.lineTo(canvas.width, panelTop);
    context.stroke();

    context.fillStyle = '#1f2937';
    context.font = `${14 * scale}px Arial`;
    context.fillText('Pinned Route Order', 12 * scale, panelTop + 24 * scale);

    context.font = `${12 * scale}px Arial`;
    if (routePosts.length === 0) {
      context.fillStyle = '#6b7280';
      context.fillText('No pinned routes yet.', 12 * scale, panelTop + 48 * scale);
    } else {
      routePosts.forEach((post, index) => {
        const lineY = panelTop + (48 + index * 24) * scale;
        const isPinned = pinnedPostIdSet.has(post.id);
        const itemLabel = `${isPinned ? '[x]' : '[ ]'} ${index + 1}. ${String(post.place || post.activity || `Post ${post.id}`)} (${String(post.iso || '').toUpperCase()})`;
        context.fillStyle = '#111827';
        context.fillText(itemLabel, 12 * scale, lineY);
      });
    }

    const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!pngBlob) {
      throw new Error('Unable to create PNG export/share blob.');
    }

    return pngBlob;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

export async function shareOrDownloadPinboardImage(
  pngBlob: Blob,
  fileName: string,
  shareTitle: string,
  shareText: string,
  shareUrl: string,
  downloadBlob: (blob: Blob, fileName: string) => void,
): Promise<void> {
  const nativeNavigator = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };

  if (typeof File !== 'undefined' && typeof nativeNavigator.share === 'function') {
    const pngFile = new File([pngBlob], fileName, { type: 'image/png' });

    if (!nativeNavigator.canShare || nativeNavigator.canShare({ files: [pngFile] })) {
      await nativeNavigator.share({
        files: [pngFile],
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      });
      return;
    }

    downloadBlob(pngBlob, fileName);
    await nativeNavigator.share({
      title: shareTitle,
      text: `${shareText} ${shareUrl}`,
      url: shareUrl,
    });
    return;
  }

  downloadBlob(pngBlob, fileName);
  const whatsappShareText = encodeURIComponent(`${shareText} ${shareUrl}`);
  window.open(`https://wa.me/?text=${whatsappShareText}`, '_blank', 'noopener,noreferrer');
}

export { waitForDoubleAnimationFrame };
